import { Component, type ErrorInfo, type ReactNode } from 'react';

// Guards the Sankey canvas so a render-time exception (e.g. d3-sankey choking on
// a degenerate graph produced by an aggressive filter) shows a recoverable
// message instead of white-screening the whole standalone page.

interface Props {
  children: ReactNode;
  onReset?: () => void;
}
interface State {
  error: Error | null;
}

export class ChartErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ChartErrorBoundary caught a render error', error, info);
  }

  private handleReset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            height: '100%',
            color: '#64748b',
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
            Couldn&apos;t render this view
          </div>
          <div style={{ fontSize: 12, maxWidth: 420 }}>
            The current filter combination produced a chart this layout
            engine can&apos;t draw. Reset the filters and try again.
          </div>
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              font: 'inherit',
              fontSize: 12,
              padding: '5px 12px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Reset filters
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
